import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { VendorService } from '@/services/vendor.service';
import { PurchaseOrder, PurchaseOrderItem, PurchaseOrderStatus, Vendor } from '@/models/vendor.model';

type FormState = Partial<PurchaseOrder>;

const makeId = () => Math.random().toString(36).slice(2, 10);

export const PurchaseOrderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  const [params] = useSearchParams();
  const preselectedVendorId = params.get('vendorId') || '';

  const [formData, setFormData] = useState<FormState>({
    vendorId: preselectedVendorId,
    vendorName: '',
    poNumber: '',
    status: PurchaseOrderStatus.draft,
    orderDate: new Date(),
    expectedDeliveryDate: undefined,
    items: [],
    subtotal: 0,
    tax: 0,
    shipping: 0,
    total: 0,
    notes: '',
  });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: existingPO, isLoading: poLoading } = useQuery({
    queryKey: ['purchaseOrder', id],
    queryFn: () => (id ? VendorService.getPurchaseOrderById(id) : null),
    enabled: isEditing && Boolean(id),
  });

  const { data: vendors = [], isLoading: vendorsLoading } = useQuery({
    queryKey: ['vendorsAll'],
    queryFn: () => VendorService.getAllVendors(),
    enabled: Boolean(profile),
  });

  const vendorById = useMemo(() => {
    const map = new Map<string, Vendor>();
    for (const v of vendors) map.set(v.id, v);
    return map;
  }, [vendors]);

  useEffect(() => {
    if (existingPO) {
      setFormData(existingPO);
      return;
    }
    if (!formData.vendorId) return;
    const v = vendorById.get(formData.vendorId);
    if (!v) return;
    setFormData((prev) => ({ ...prev, vendorName: v.name }));
  }, [existingPO, formData.vendorId, vendorById]);

  const recalcTotals = (nextItems: PurchaseOrderItem[], tax: number, shipping: number) => {
    const subtotal = nextItems.reduce((sum, i) => sum + (i.total || 0), 0);
    const total = subtotal + (tax || 0) + (shipping || 0);
    setFormData((prev) => ({ ...prev, items: nextItems, subtotal, total }));
  };

  const addItem = () => {
    const next: PurchaseOrderItem = { id: makeId(), description: '', quantity: 1, unitPrice: 0, total: 0, category: '' };
    const items = [...(formData.items || []), next];
    recalcTotals(items, formData.tax || 0, formData.shipping || 0);
  };

  const updateItem = (id: string, updates: Partial<PurchaseOrderItem>) => {
    const items = (formData.items || []).map((i) => {
      if (i.id !== id) return i;
      const merged = { ...i, ...updates };
      const qty = Number(merged.quantity) || 0;
      const unit = Number(merged.unitPrice) || 0;
      return { ...merged, quantity: qty, unitPrice: unit, total: qty * unit };
    });
    recalcTotals(items, formData.tax || 0, formData.shipping || 0);
  };

  const removeItem = (id: string) => {
    const items = (formData.items || []).filter((i) => i.id !== id);
    recalcTotals(items, formData.tax || 0, formData.shipping || 0);
  };

  const mutation = useMutation({
    mutationFn: async (data: FormState) => {
      if (!profile?.id) throw new Error(`You must be logged in to ${isEditing ? 'update' : 'create'} a purchase order`);
      if (!data.vendorId) throw new Error('Vendor is required');
      if (!data.vendorName) throw new Error('Vendor name is required');

      const now = new Date();
      const poNumber = data.poNumber?.trim() || `PO-${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}-${makeId().toUpperCase()}`;

      if (isEditing && id) {
        await VendorService.updatePurchaseOrder(id, {
          ...data,
          poNumber,
        });
        return id;
      } else {
        return await VendorService.createPurchaseOrder({
          ...data,
          poNumber,
          createdBy: profile.id,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchaseOrders'] });
      navigate(isEditing && id ? `/vendors/purchase-orders/${id}` : '/vendors');
    },
    onError: (error: unknown) => {
      setErrorMessage(error instanceof Error ? error.message : `Failed to ${isEditing ? 'update' : 'create'} purchase order`);
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    try {
      await mutation.mutateAsync(formData);
    } catch (err) {
      console.error('[PurchaseOrderForm] Submit error:', err);
    }
  };

  if (poLoading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">{isEditing ? 'Edit Purchase Order' : 'Create Purchase Order'}</h1>

      <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg p-6 space-y-6">
        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700">Vendor *</label>
            <select
              required
              disabled={vendorsLoading}
              value={formData.vendorId || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, vendorId: e.target.value }))}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50"
            >
              <option value="" disabled>
                Select a vendor
              </option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Status</label>
            <select
              value={formData.status || PurchaseOrderStatus.draft}
              onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value as PurchaseOrderStatus }))}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.values(PurchaseOrderStatus).map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">PO Number</label>
            <input
              type="text"
              value={formData.poNumber || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, poNumber: e.target.value }))}
              placeholder="Auto-generated if blank"
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Order Date</label>
            <input
              type="date"
              value={formData.orderDate ? new Date(formData.orderDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, orderDate: e.target.value ? new Date(e.target.value) : new Date() }))}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Expected Delivery</label>
            <input
              type="date"
              value={formData.expectedDeliveryDate ? new Date(formData.expectedDeliveryDate).toISOString().split('T')[0] : ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, expectedDeliveryDate: e.target.value ? new Date(e.target.value) : undefined }))
              }
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between gap-3 mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Line Items</h2>
            <button
              type="button"
              onClick={addItem}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              + Add Item
            </button>
          </div>

          {(formData.items || []).length === 0 ? (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-4 text-sm text-gray-600">
              Add at least one line item to calculate totals.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Description</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-700">Category</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Qty</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Unit</th>
                    <th className="px-3 py-2 text-right font-medium text-gray-700">Total</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(formData.items || []).map((item) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={item.description || ''}
                          onChange={(e) => updateItem(item.id, { description: e.target.value })}
                          className="w-full border border-gray-300 rounded-md py-1.5 px-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={item.category || ''}
                          onChange={(e) => updateItem(item.id, { category: e.target.value })}
                          className="w-full border border-gray-300 rounded-md py-1.5 px-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, { quantity: Number(e.target.value) })}
                          className="w-24 border border-gray-300 rounded-md py-1.5 px-2 text-right focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={item.unitPrice}
                          onChange={(e) => updateItem(item.id, { unitPrice: Number(e.target.value) })}
                          className="w-28 border border-gray-300 rounded-md py-1.5 px-2 text-right focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">${(item.total || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right">
                        <button type="button" onClick={() => removeItem(item.id)} className="text-sm text-red-600 hover:text-red-800">
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Tax</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={formData.tax || 0}
              onChange={(e) => {
                const tax = Number(e.target.value) || 0;
                setFormData((prev) => ({ ...prev, tax }));
                recalcTotals(formData.items || [], tax, formData.shipping || 0);
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Shipping</label>
            <input
              type="number"
              min={0}
              step="0.01"
              value={formData.shipping || 0}
              onChange={(e) => {
                const shipping = Number(e.target.value) || 0;
                setFormData((prev) => ({ ...prev, shipping }));
                recalcTotals(formData.items || [], formData.tax || 0, shipping);
              }}
              className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
            <div className="text-sm text-gray-600">Total</div>
            <div className="text-2xl font-bold text-gray-900">${(formData.total || 0).toLocaleString()}</div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <textarea
            value={formData.notes || ''}
            onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
            rows={4}
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(isEditing && id ? `/vendors/purchase-orders/${id}` : '/vendors')}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending || vendorsLoading}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving...' : isEditing ? 'Update PO' : 'Create PO'}
          </button>
        </div>
      </form>
    </div>
  );
};

